import { send_request } from "../utils/Request";

const YAML_HEADERS = {
  Accept: "application/yaml, text/yaml",
  "Content-Type": "application/yaml",
};

function assertSuccessfulResponse(response, fallbackMessage) {
  if (response?.response) throw response;
  if (!response || response.status < 200 || response.status >= 300) {
    throw new Error(fallbackMessage);
  }
  return response;
}

function filenameFromDisposition(disposition, fallback) {
  const extended = disposition?.match(/filename\*\s*=\s*([^;]+)/i)?.[1];
  if (extended) {
    const encoded = extended
      .trim()
      .replace(/^"|"$/g, "")
      .replace(/^UTF-8''/i, "");
    try {
      return decodeURIComponent(encoded);
    } catch (_error) {
      // Fall through to the basic filename or local fallback.
    }
  }

  const match = disposition?.match(/filename="?([^";]+)"?/i);
  const filename = match?.[1];
  return filename && !filename.startsWith("=?") ? filename : fallback;
}

export function workflowDefinitionFilename(name) {
  const slug = String(name || "untitled-workflow")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${slug || "untitled-workflow"}.workflow.yaml`;
}

export async function fetchWorkflowDefinitionYaml(
  projectId,
  { published = false, version, fallbackName } = {}
) {
  const response = assertSuccessfulResponse(
    await send_request(
      `project-service/project/${projectId}/definition.yaml`,
      null,
      { published: published || undefined, version },
      "get",
      YAML_HEADERS,
      "text"
    ),
    "Workflow YAML could not be loaded."
  );

  return {
    yaml: response.data,
    filename: filenameFromDisposition(
      response.headers?.["content-disposition"],
      workflowDefinitionFilename(fallbackName)
    ),
  };
}

export async function importWorkflowDefinitionYaml(
  yaml,
  { name, parentFolderId, workspaceId } = {}
) {
  const response = assertSuccessfulResponse(
    await send_request(
      "project-service/project/import-definition",
      yaml,
      { name, parentFolderId, workspaceId },
      "post",
      YAML_HEADERS
    ),
    "Workflow YAML could not be imported."
  );
  return response.data;
}

export function downloadWorkflowDefinition(yaml, filename) {
  const url = URL.createObjectURL(
    new Blob([yaml], { type: "application/yaml;charset=utf-8" })
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function workflowDefinitionErrorMessage(error) {
  const data = error?.response?.data;
  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return data.errors
      .map((item) => item.message || item.code)
      .filter(Boolean)
      .join("\n");
  }
  return (
    data?.message ||
    data?.error ||
    error?.message ||
    "Workflow YAML could not be imported."
  );
}
